import { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Checkbox, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  AntDatePicker,
  AntInputNumber,
  AntSelect,
  AntSelectOption,
  AntTextarea,
  Attachment,
  CompensationTypeHelper,
  UploadButton,
  useAvailablePeriods,
  useCompensationFormItems,
  useStatusOptions,
} from 'components/common';
import { getRules, getUser } from 'store';
import { useAppSelector, useCompensationTypes, useEmployeeOptions, useFetchSelector } from 'hooks';
import { fetchEmployeeData, fetchRules } from 'services';
import { CompensationStatus, CompensationType, LegalEntity, Namespace } from 'enums';
import {
  currencies,
  MIN_DATE_FOR_BILLED_ON,
  MAX_DATE_FOR_BILLED_ON,
  REVERSE_DATE_FORMAT,
  DATE_PERIOD_FORMAT,
} from 'constants-module';
import { Compensation, EditableCompensation, Rule } from 'types';

import styles from './CompensationForm.module.scss';
import { useAttachmentHandlers, useCompensationForm, useEndOfQuarterPeriods, useCompensationTypePolicy } from './hooks';
import { getConsentFieldPolicy, getConsentTextKeys } from './consent-policy.config';
import { CompensationViewField, CompensationViewRow } from '../../../Details/components/CompensationViewField';
import { capitalize } from '../../../../helpers';
import { Info } from '../../../../icons';

const { Title, Paragraph } = Typography;

interface Props {
  readonly compensation?: Compensation;
  readonly isNew?: boolean;
  readonly isUserMode?: boolean;
  readonly formId?: string;
  readonly submit: (compensation: EditableCompensation) => Promise<void>;
  readonly close?: () => void;
  readonly onDisabledChange?: (disabled: boolean) => void;
  readonly isAttachmentsExpired?: boolean;
}

export const CompensationForm = ({
  compensation,
  isNew,
  isUserMode,
  formId,
  submit,
  onDisabledChange,
  isAttachmentsExpired,
}: Props) => {
  const { t } = useTranslation(Namespace.CompensationForm);
  const rules = useFetchSelector(getRules, fetchRules);
  const user = useAppSelector(getUser);
  const [entity, setEntity] = useState<LegalEntity>();
  const [employeeId, setEmployeeId] = useState<number>();

  const filteredRules: Rule[] = useMemo(() => {
    return rules.filter((item: Rule) => item.legalEntity === entity);
  }, [entity, rules]);

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    const requestId = ++requestIdRef.current;

    const load = async () => {
      if (employeeId === undefined) return;

      try {
        const { legalEntity } = await fetchEmployeeData(employeeId);

        if (isMountedRef.current && requestId === requestIdRef.current) {
          setEntity(legalEntity);
        }
      } catch {
        /* errors handled elsewhere */
      }
    };

    load();

    return () => {
      isMountedRef.current = false;
    };
  }, [employeeId]);

  const {
    form,
    type,
    status,
    rule,
    attachments,
    newAttachments,
    setAttachments,
    setNewAttachments,
    getUpdatedCompensation,
  } = useCompensationForm(compensation, filteredRules);

  const {
    employeeFormItemProps,
    typeFormItemProps,
    amountFormItemProps,
    currencyFormItemProps,
    billedOnFormItemProps,
    targetPeriodFormItemProps,
    targetPeriodEditorFormItemProps,
    employeeCommentFormItemProps,
    reviewerCommentFormItemProps,
    statusFormItemProps,
    attachmentsCounterFormItemProps,
  } = useCompensationFormItems(rule, status);

  const employees = useEmployeeOptions();
  const compensationTypes = useCompensationTypes(isUserMode);
  const defaultPeriods = useAvailablePeriods(!isNew);
  const endOfQuarterPeriods = useEndOfQuarterPeriods();
  const statuses = useStatusOptions(compensation?.status === CompensationStatus.Failed);
  const benefitType = Form.useWatch('type', form);
  const consentChecked = Form.useWatch('privacyConsent', form);
  const { shouldUseQuarterPeriods, shouldHideRestrictedTypes, isTypeSelectDisabled, requiresConsentType } =
    useCompensationTypePolicy({
      benefitType,
      compensationType: compensation?.type,
      isNew,
      isUserMode,
    });
  const { isRequired: requireConsent, isDisabled: isConsentDisabled } = getConsentFieldPolicy({
    requiresConsentType,
    isUserMode,
    isNew,
    privacyConsent: compensation?.privacyConsent,
  });
  const consentKeys = getConsentTextKeys(benefitType ?? compensation?.type);
  const periods = shouldUseQuarterPeriods ? endOfQuarterPeriods! : defaultPeriods;

  const compensationTypesFiltered = useMemo(() => {
    return shouldHideRestrictedTypes
      ? compensationTypes.filter(
          (opt) => opt.value !== CompensationType.Medical && opt.value !== CompensationType.Robotics,
        )
      : compensationTypes;
  }, [compensationTypes, shouldHideRestrictedTypes]);

  const periodsWithCurrentValue = useMemo((): AntSelectOption[] => {
    const currentValue = compensation?.targetPeriod
      ? {
          label: capitalize(dayjs(compensation.targetPeriod).format(DATE_PERIOD_FORMAT)),
          value: dayjs(compensation.targetPeriod).format(REVERSE_DATE_FORMAT),
        }
      : null;

    if (!periods) {
      return currentValue ? [currentValue] : [];
    }

    const isPeriodsExist = periods.find((period) => period.value === currentValue?.value);

    return isPeriodsExist || !currentValue ? periods : [currentValue, ...periods];
  }, [compensation, periods]);

  const [attachmentError, setAttachmentError] = useState<string | undefined>(undefined);
  const [attachmentCountError, setAttachmentCountError] = useState<string | undefined>(undefined);
  const [invalidAttachmentErrors, setInvalidAttachmentErrors] = useState<Record<string, string>>({});
  const { handleAddAttachment, handleRemoveAttachment } = useAttachmentHandlers(
    form,
    setAttachmentError,
    setInvalidAttachmentErrors,
    setAttachmentCountError,
  );

  const employeeName = useMemo(
    () => employees.find(({ value }) => compensation?.employeeID === value)?.label,
    [compensation, employees],
  );

  useEffect(() => {
    setEmployeeId(compensation?.employeeID || user!.employeeID);
  }, [compensation?.employeeID, user]);

  useEffect(() => {
    if (isNew) {
      form.setFieldValue('privacyConsent', false);
    }
  }, [benefitType, form, isNew]);

  useEffect(() => {
    const hasPerFileErrors = Object.keys(invalidAttachmentErrors || {}).length > 0;
    const hasAttachmentError = !!attachmentError || !!attachmentCountError || hasPerFileErrors;

    const submitDisabled = (requireConsent && !consentChecked) || hasAttachmentError;

    onDisabledChange?.(submitDisabled);
  }, [
    requireConsent,
    consentChecked,
    form,
    onDisabledChange,
    attachments,
    newAttachments,
    attachmentError,
    attachmentCountError,
    invalidAttachmentErrors,
  ]);

  return (
    <Form
      id={formId}
      className={styles.form}
      layout="vertical"
      form={form}
      onFinish={() => submit(getUpdatedCompensation())}
    >
      {!isNew && (
        <CompensationViewField>
          <div className="compensation-view__title">{t('employee')}</div>
          <div className="compensation-view__value">{employeeName}</div>
        </CompensationViewField>
      )}

      {isNew && !isUserMode && (
        <AntSelect
          id="employeeID"
          formItemProps={employeeFormItemProps}
          placeholder={t('employeePlaceholder')}
          options={employees}
          onChange={setEmployeeId}
        />
      )}

      <AntSelect
        id="type"
        formItemProps={typeFormItemProps}
        options={compensationTypesFiltered}
        disabled={isTypeSelectDisabled || (!isNew && isUserMode)}
      />

      {isNew && type && <CompensationTypeHelper type={type} />}

      <div className={styles.row}>
        <AntInputNumber
          id="amount"
          formItemProps={amountFormItemProps}
          placeholder={!rule || rule.requiresBill ? t('billAmountPlaceholder') : t('paymentAmountPlaceholder')}
          disabled={rule?.fixedAmount}
        />

        <AntSelect
          id="currency"
          formItemProps={currencyFormItemProps}
          options={currencies}
          disabled={rule?.fixedAmount}
        />

        {rule?.requiresBill && (
          <AntDatePicker
            id="billedOn"
            formItemProps={billedOnFormItemProps}
            placeholder={t('select')}
            maxDate={MAX_DATE_FOR_BILLED_ON}
            minDate={MIN_DATE_FOR_BILLED_ON}
          />
        )}
      </div>

      {!isUserMode && !isNew ? (
        <AntSelect
          id="targetPeriods"
          formItemProps={targetPeriodEditorFormItemProps}
          options={periodsWithCurrentValue}
          mode="multiple"
        />
      ) : (
        <AntSelect id="targetPeriod" formItemProps={targetPeriodFormItemProps} options={periodsWithCurrentValue} />
      )}

      <AntTextarea
        id="employeeComment"
        formItemProps={employeeCommentFormItemProps}
        placeholder={t('employeeCommentPlaceholder')}
        disabled={!isNew && !isUserMode}
      />

      {!isNew && (
        <AntTextarea
          id="reviewerComment"
          formItemProps={reviewerCommentFormItemProps}
          placeholder={t('reviewerCommentPlaceholder')}
          disabled={isUserMode}
        />
      )}

      {!isNew && (
        <AntSelect
          id="status"
          className={styles.statusSelect}
          formItemProps={statusFormItemProps}
          optionFilterProp="key"
          options={statuses}
          allowClear={false}
          disabled={isUserMode}
        />
      )}

      <CompensationViewField>
        <div className="compensation-view__title">{t('attachments')}</div>
        <Form.Item {...attachmentsCounterFormItemProps}>
          <UploadButton
            id="newAttachments"
            name="newAttachments"
            label={t('attachFile')}
            addAttachment={handleAddAttachment(setNewAttachments, newAttachments, attachments)}
          />
        </Form.Item>
        <CompensationViewRow className="compensation-view__value">
          {isAttachmentsExpired && attachments?.length ? (
            <span className={styles.deletedAttachmentMessage}>{t('expiredAttachments')}</span>
          ) : (
            attachments?.map((a) => (
              <Attachment
                key={a.attachmentID}
                fileName={a.originalName}
                attachmentId={a.attachmentID}
                requestId={compensation?.compensationRequestID}
                handleRemoveAttachment={handleRemoveAttachment(setAttachments, attachments, newAttachments)}
                isEdit
              />
            ))
          )}
          {newAttachments?.map((attachment) => (
            <Attachment
              key={`${attachment.name}-${attachment.lastModified}-${attachment.size}`}
              fileName={attachment.name}
              file={attachment}
              handleRemoveAttachment={handleRemoveAttachment(setNewAttachments, newAttachments, attachments)}
              isEdit
              isInvalid={!!invalidAttachmentErrors[attachment.name]}
              fileError={invalidAttachmentErrors[attachment.name]}
            />
          ))}
        </CompensationViewRow>

        {attachmentCountError && <div className={styles.attachmentError}>{attachmentCountError}</div>}
        {attachmentError && <div className={styles.attachmentError}>{attachmentError}</div>}
      </CompensationViewField>

      {requiresConsentType && (
        <div className={styles.consentBlock}>
          <Form.Item
            name="privacyConsent"
            valuePropName="checked"
            rules={
              requireConsent
                ? [
                    {
                      validator: (_rule, value) =>
                        value === true ? Promise.resolve() : Promise.reject(new Error(t('consentRequiredError'))),
                    },
                  ]
                : []
            }
          >
            <Checkbox className={styles.consentLabel} disabled={isConsentDisabled}>
              {t(consentKeys.label)}
              <Tooltip
                title={
                  <>
                    <Title level={5}>{t('consentNoteLabel')}</Title>
                    <Paragraph>{t(consentKeys.purpose)}</Paragraph>
                    <Paragraph>{t(consentKeys.data)}</Paragraph>
                    <Paragraph>{t(consentKeys.law)}</Paragraph>
                  </>
                }
                color="white"
                classNames={{ root: styles.consentNote }}
                placement="right"
              >
                <Info />
              </Tooltip>
            </Checkbox>
          </Form.Item>
        </div>
      )}
    </Form>
  );
};
